import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth, API } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Plus, FileText, Edit, Trash2, LogOut, Loader2, AlertCircle, ShieldAlert, FileDown, MapPin, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import SiteFooter from '../components/SiteFooter';
import ModonLogo from '../components/ModonLogo';
import LanguageSwitcher from '../components/LanguageSwitcher';

const RISK_BADGES = {
  high: { label: 'عالي', cls: 'bg-red-600' },
  medium: { label: 'متوسط', cls: 'bg-amber-500' },
  low: { label: 'منخفض', cls: 'bg-emerald-600' },
};

function RiskBadge({ risk }) {
  if (!risk) return null;
  const cfg = RISK_BADGES[risk.level] || RISK_BADGES.low;
  return (
    <Badge className={`${cfg.cls} text-white`} data-testid={`risk-${risk.level}`}>
      <ShieldAlert size={11} className="ml-1"/> {risk.labelAr || cfg.label} ({risk.score})
    </Badge>
  );
}

export default function FactoryDashboard() {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [delReqOpen, setDelReqOpen] = useState(null); // sds id
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/sds`);
      setItems(r.data || []);
    } catch (e) {
      toast.error('تعذر تحميل التقارير');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const requestDelete = async () => {
    if (!delReqOpen) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/sds/${delReqOpen}/request-delete`, { reason });
      toast.success('تم إرسال طلب الحذف لمسؤول النظام');
      setDelReqOpen(null);
      setReason('');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'تعذر إرسال الطلب');
    } finally {
      setSubmitting(false);
    }
  };

  const cancelRequest = async (id) => {
    try {
      await axios.delete(`${API}/sds/${id}/request-delete`);
      toast.success('تم إلغاء طلب الحذف');
      load();
    } catch {
      toast.error('تعذر إلغاء الطلب');
    }
  };

  return (
    <div className="modon-app-shell flex flex-col" dir={i18n.language === 'en' ? 'ltr' : 'rtl'}>
      <header className="modon-header">
        <div className="modon-header-inner max-w-6xl">
          <div className="flex items-center gap-3">
            <ModonLogo size={44}/>
            <div>
              <h1 className="font-extrabold text-slate-900" data-testid="factory-name-header">{user?.factoryName}</h1>
              <p className="text-xs text-slate-500">{user?.activity} • {user?.city}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <LanguageSwitcher/>
            <Link to="/factory/new">
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="new-sds-header-btn">
                <Plus size={14} className="mx-1"/> {t('factory.newReport')}
              </Button>
            </Link>
            <Link to="/factory/my-map">
              <Button size="sm" variant="outline" data-testid="my-map-btn">
                <MapPin size={14} className="mx-1"/> {t('factory.myMap')}
              </Button>
            </Link>
            <Button size="sm" variant="outline" onClick={() => { logout(); nav('/login'); }} data-testid="factory-logout-btn">
              <LogOut size={14} className="mx-1" /> {t('common.logout')}
            </Button>
          </div>
        </div>
      </header>
      <main className="flex-1 max-w-6xl mx-auto w-full px-4 py-6">
        <h2 className="text-lg font-bold text-slate-800 mb-3">{t('factory.myReports')}</h2>
        {loading ? (
          <div className="flex items-center gap-2 text-slate-600"><Loader2 className="animate-spin" size={16}/> {t('factory.loading')}</div>
        ) : items.length === 0 ? (
          <Card className="p-8 text-center text-slate-600">
            <FileText className="mx-auto mb-2 text-slate-400" size={36} />
            <div>{t('factory.noReports')}. {t('factory.noReportsHint')}.</div>
            <Link to="/factory/new">
              <Button className="mt-3 bg-[#1a4d80] hover:bg-[#143d68]"><Plus size={14} className="ml-1" /> {t('factory.newReport')}</Button>
            </Link>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {items.map((it) => (
              <Card
                key={it.id}
                className="overflow-hidden hover:shadow-lg transition-shadow flex flex-col group"
                data-testid={`sds-card-${it.id}`}
              >
                {/* Thumbnail preview of the actual SDS PDF/template */}
                <Link to={`/factory/pdf/${it.id}`} className="block relative bg-slate-100 border-b" style={{ aspectRatio: '210 / 297' }}>
                  {it.thumbnail ? (
                    <img
                      src={it.thumbnail}
                      alt={it.title}
                      className="absolute inset-0 w-full h-full object-cover object-top"
                      data-testid={`sds-thumb-${it.id}`}
                    />
                  ) : (
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-400 p-4">
                      <FileText size={48} className="mb-2" />
                      <span className="text-xs text-center">{t('factory.previewUnavailable')}<br/>{t('factory.previewTapToResave')}</span>
                    </div>
                  )}
                  {/* Hover overlay with "View PDF" */}
                  <div className="absolute inset-0 bg-[#1a4d80]/0 group-hover:bg-[#1a4d80]/60 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                    <div className="bg-white rounded-full px-4 py-2 shadow-lg text-sm font-bold text-[#1a4d80] flex items-center gap-1">
                      <FileDown size={16}/> {t('factory.viewPdf')}
                    </div>
                  </div>
                  {/* Risk badge floating top-right */}
                  <div className="absolute top-2 right-2">
                    <RiskBadge risk={it.risk}/>
                  </div>
                  {it.deletion_requested && (
                    <div className="absolute top-2 left-2">
                      <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-400 text-[10px]">
                        <AlertCircle size={10} className="ml-1"/> {t('factory.pendingDeletion')}
                      </Badge>
                    </div>
                  )}
                </Link>

                {/* Card footer */}
                <div className="p-3 flex flex-col gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText className="text-[#1a4d80] flex-shrink-0" size={16}/>
                    <h3 className="font-bold text-slate-900 text-sm truncate">{it.title || t('factory.untitled')}</h3>
                  </div>
                  <div className="text-xs text-slate-500 flex items-center justify-between">
                    <span>{t('factory.chemicalsCount', { count: it.chemicalsCount })}</span>
                    <span>{it.updated_at ? new Date(it.updated_at).toLocaleDateString(i18n.language === 'en' ? 'en-US' : 'ar-SA') : '—'}</span>
                  </div>
                  <div className="flex gap-1">
                    <Link to={`/factory/pdf/${it.id}`} className="flex-1">
                      <Button size="sm" className="w-full text-xs bg-[#1a4d80] hover:bg-[#143d68] text-white" data-testid={`view-pdf-${it.id}`}>
                        <FileDown size={12} className="ml-1"/> {t('factory.viewPdf')}
                      </Button>
                    </Link>
                    <Link to={`/factory/edit/${it.id}`}>
                      <Button size="sm" variant="outline" className="text-xs" data-testid={`edit-sds-${it.id}`}>
                        <Edit size={12}/>
                      </Button>
                    </Link>
                    {it.deletion_requested ? (
                      <Button size="sm" variant="outline" onClick={() => cancelRequest(it.id)} className="text-amber-700 text-xs" data-testid={`cancel-delete-req-${it.id}`} title={t('factory.cancelDeletion')}>
                        <AlertCircle size={12}/>
                      </Button>
                    ) : (
                      <Button size="sm" variant="outline" onClick={() => setDelReqOpen(it.id)} className="text-red-600 hover:bg-red-50 text-xs" data-testid={`request-delete-${it.id}`} title={t('factory.deleteRequest')}>
                        <Trash2 size={12} />
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Deletion request dialog */}
      <Dialog open={!!delReqOpen} onOpenChange={(o) => { if (!o) { setDelReqOpen(null); setReason(''); } }}>
        <DialogContent dir={i18n.language === 'en' ? 'ltr' : 'rtl'}>
          <DialogHeader>
            <DialogTitle>{t('factory.deletionDialogTitle')}</DialogTitle>
            <DialogDescription>
              {t('factory.deletionDialogBody')}
            </DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder={t('factory.deletionReasonPh')}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            data-testid="delete-reason-input"
          />
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setDelReqOpen(null); setReason(''); }}>{t('common.cancel')}</Button>
            <Button onClick={requestDelete} disabled={submitting} className="bg-red-600 hover:bg-red-700" data-testid="submit-delete-request-btn">
              {submitting ? <Loader2 className="animate-spin ml-1" size={14}/> : null} {t('factory.sendRequest')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <SiteFooter />
    </div>
  );
}
